import { i18n, I18n } from '@lingui/core';
import { useLingui } from '@lingui/react';
import { I18nText } from '@/api/common-types/i18n_text';
import invariant from 'tiny-invariant';
import LinguiConfig from '../lingui.config.js';
import { STORAGE_KEYS } from '@/lib';
import * as Sentry from '@sentry/nextjs';

export enum LOCALE {
  EN = 'en',
  ES = 'es',
  PT_BR = 'pt-br',
  AR = 'ar',
  DE = 'de',
  CS = 'cs',
  SK = 'sk',
  HR = 'hr',
  PL = 'pl',
  HU = 'hu',
  RO = 'ro',
  BG = 'bg',
  TR = 'tr',
  IT = 'it',
  FR = 'fr',
}

export enum DATA_LOCALE {
  EN = 'en',
  FR = 'fr',
  ES = 'es',
  DE = 'de',
  PT = 'pt',
  BN = 'bn',
  AR = 'ar',
  ID = 'id',
  EN_US = 'en_US',
  ZH = 'zh',
  CS = 'cs',
  SK = 'sk',
  HR = 'hr',
  PL = 'pl',
  HU = 'hu',
  RO = 'ro',
  BG = 'bg',
  TR = 'tr',
  IT = 'it',
}

invariant(Object.keys(LOCALE).length === LinguiConfig.locales.length, 'Missing locale');

export const DEFAULT_LOCALE = LOCALE.EN;
export const DEFAULT_DATA_LOCALE = DATA_LOCALE.EN;

export function isLocale(value: unknown): value is LOCALE {
  return (Object.values(LOCALE) as unknown[]).includes(value);
}

export function isDataLocale(value: unknown): value is DATA_LOCALE {
  return (Object.values(DATA_LOCALE) as unknown[]).includes(value);
}

const UI_TO_DATA_LOCALE: Record<LOCALE, DATA_LOCALE> = {
  [LOCALE.EN]: DATA_LOCALE.EN,
  [LOCALE.ES]: DATA_LOCALE.ES,
  [LOCALE.PT_BR]: DATA_LOCALE.PT,
  [LOCALE.AR]: DATA_LOCALE.AR,
  [LOCALE.DE]: DATA_LOCALE.DE,
  [LOCALE.CS]: DATA_LOCALE.CS,
  [LOCALE.SK]: DATA_LOCALE.SK,
  [LOCALE.HR]: DATA_LOCALE.HR,
  [LOCALE.PL]: DATA_LOCALE.PL,
  [LOCALE.HU]: DATA_LOCALE.HU,
  [LOCALE.RO]: DATA_LOCALE.RO,
  [LOCALE.BG]: DATA_LOCALE.BG,
  [LOCALE.TR]: DATA_LOCALE.TR,
  [LOCALE.IT]: DATA_LOCALE.IT,
  [LOCALE.FR]: DATA_LOCALE.FR,
};

export const useLocaleDisplayText = (i18n: I18n) => {
  const localeDisplayText = {
    [LOCALE.EN]: i18n.t('English'),
    [LOCALE.ES]: i18n.t('Spanish'),
    [LOCALE.PT_BR]: i18n.t('Portuguese'),
    [LOCALE.AR]: i18n.t('Arabic'),
    [LOCALE.DE]: i18n.t('German'),
    [LOCALE.CS]: i18n.t('Czech'),
    [LOCALE.SK]: i18n.t('Slovak'),
    [LOCALE.HR]: i18n.t('Croatian'),
    [LOCALE.PL]: i18n.t('Polish'),
    [LOCALE.HU]: i18n.t('Hungarian'),
    [LOCALE.RO]: i18n.t('Romanian'),
    [LOCALE.BG]: i18n.t('Bulgarian'),
    [LOCALE.TR]: i18n.t('Turkish'),
    [LOCALE.IT]: i18n.t('Italian'),
    [LOCALE.FR]: i18n.t('French'),
  };

  invariant(
    Object.keys(localeDisplayText).length === LinguiConfig.locales.length,
    'Missing locale display text'
  );

  return localeDisplayText;
};

export const useDataLocaleDisplayText = (i18n: I18n) => {
  const localeDisplayText = {
    [DATA_LOCALE.EN]: i18n.t('English'),
    [DATA_LOCALE.FR]: i18n.t('French'),
    [DATA_LOCALE.ES]: i18n.t('Spanish'),
    [DATA_LOCALE.DE]: i18n.t('German'),
    [DATA_LOCALE.PT]: i18n.t('Portuguese'),
    [DATA_LOCALE.BN]: i18n.t('Bengali, Bangla'),
    [DATA_LOCALE.AR]: i18n.t('Arabic'),
    [DATA_LOCALE.ID]: i18n.t('Indonesian'),
    [DATA_LOCALE.EN_US]: i18n.t('English (United States)'),
    [DATA_LOCALE.ZH]: i18n.t('Chinese (Simplified)'),
    [DATA_LOCALE.CS]: i18n.t('Czech'),
    [DATA_LOCALE.SK]: i18n.t('Slovak'),
    [DATA_LOCALE.HR]: i18n.t('Croatian'),
    [DATA_LOCALE.PL]: i18n.t('Polish'),
    [DATA_LOCALE.HU]: i18n.t('Hungarian'),
    [DATA_LOCALE.RO]: i18n.t('Romanian'),
    [DATA_LOCALE.BG]: i18n.t('Bulgarian'),
    [DATA_LOCALE.TR]: i18n.t('Turkish'),
    [DATA_LOCALE.IT]: i18n.t('Italian'),
  };

  return localeDisplayText;
};

export function getUserLocale(): string | null {
  const locale = localStorage.getItem(STORAGE_KEYS.USER_LANGUAGE);

  return locale;
}

export async function activateUserLocale() {
  /*
   * This function is used to activate the user's preferred language.
   * It first checks if the user has a preferred language stored in local storage.
   * If not, it defaults to the defaultLocale.
   * It then loads the messages for the user's preferred language and activates it.
   */

  // The Knockout application uses local storage to store the user's preferred language.
  const userLanguageLocale = getUserLocale() || DEFAULT_LOCALE;
  const { messages } = await import(`./locales/${userLanguageLocale}/messages`);
  i18n.load(userLanguageLocale, messages);
  i18n.activate(userLanguageLocale);

  // Store the user's preferred language for Django
  document.cookie = `django_language=${userLanguageLocale}; path=/;`;
}

export function useUserLocale() {
  const userLanguageLocale = getUserLocale() || DEFAULT_LOCALE;

  return userLanguageLocale;
}

export function useDefaultDataLocale() {
  const userLocale = getUserLocale();

  if (!userLocale) {
    return DEFAULT_DATA_LOCALE;
  }

  if (isLocale(userLocale)) {
    return UI_TO_DATA_LOCALE[userLocale];
  }

  if (isDataLocale(userLocale)) {
    return userLocale;
  }

  Sentry.captureMessage(`Invalid user locale: ${userLocale}`, 'error');
  return DEFAULT_DATA_LOCALE;
}

export type TranslateI18nText = (text?: I18nText) => string;

/**
 * useTranslateI18nText is a custom hook that provides a translation function to retrieve a translated value
 * from I18nText JSON object based on the locale key.
 * The current locale key set by the user is detected via the Lingui library.
 */
export function useTranslateI18nText() {
  const { i18n } = useLingui();

  const translateFunction = (text?: I18nText) => {
    if (!text) {
      return '';
    }
    if (typeof text === 'string') {
      return i18n._(text);
    }

    const localized = isDataLocale(i18n.locale) ? text[i18n.locale] : undefined;

    return localized || text[text.default] || '';
  };
  return translateFunction;
}

export function createEmptyI18nText(defaultLocale: DATA_LOCALE): I18nText {
  return {
    [defaultLocale]: '',
    default: defaultLocale,
  };
}

/**
 * Converts language codes to a standardized format.
 * Examples:
 * - EN -> EN
 * - EN_US -> EN (US)
 * - PT_BR -> PT (BR)
 * - CH -> CH
 *
 * @param code The language code to convert
 * @returns The formatted language code string
 */
export function formatLanguageCode(code: string): string {
  if (!code) {
    return '';
  }

  const parts = code.split('_');

  if (parts.length === 1) {
    return parts[0];
  }

  return `${parts[0]} (${parts[1]})`;
}
